import { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertTriangle, Calculator, Send, Check } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { apiClient, extractErrorMessage } from '@/lib/api';

interface ShiftPreview {
  driverId: string;
  shiftDate: string;
  driverName: string | null;
  vehiclePlate: string | null;
  tripsCount: number;
  tripsRevenueSar: number;
  totalDistanceKm: number;
  fuelCostSar: number;
  expenseTotalSar: number;
  deductionTotalSar: number;
  expectedCashSar: number;
  existing: { id: string; status: string; actualCashHandedSar: number | null; varianceSar: number | null; cashCollectedSar: number; cardCollectedSar: number; notes: string | null; submittedAt: string | null; depositPhotoUrl: string | null } | null;
}

const fmtSar = (n: number) => 'SAR ' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function DailyClosingPage() {
  const { driverId } = useParams<{ driverId: string }>();
  const [data, setData] = useState<ShiftPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actual, setActual] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!driverId) return;
    try {
      const r = await apiClient.get<ShiftPreview>('/api/v1/daily-closings/today/' + driverId);
      setData(r.data);
      if (r.data.existing) {
        setActual(String(r.data.existing.actualCashHandedSar ?? ''));
        setNotes(r.data.existing.notes ?? '');
      } else {
        setActual(String(Math.max(0, Math.round(r.data.expectedCashSar * 100) / 100)));
      }
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [driverId]);

  useEffect(() => { refresh(); }, [refresh]);

  const variance = data ? Number(actual || 0) - data.expectedCashSar : 0;

  async function submit() {
    if (!data) return;
    setBusy(true);
    try {
      await apiClient.post('/api/v1/daily-closings/submit', {
        driverId: data.driverId,
        shiftDate: data.shiftDate,
        actualCashHandedSar: Number(actual),
        notes: notes.trim() || undefined,
      });
      await refresh();
    } catch (err) {
      window.alert(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (error || !data) return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 p-6">
      <AlertTriangle className="h-8 w-8 text-amber-500" />
      <div className="text-sm text-muted-foreground">{error ?? 'No data'}</div>
    </div>
  );

  const submitted = data.existing && data.existing.status !== 'pending';
  const flagged = Math.abs(variance) > 50;

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Back</Button>
          </Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><Calculator className="h-4 w-4" /> End-of-day closing</div>
          <div className="w-12" />
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-4 space-y-4">
        <div className="rounded-lg border border-border p-3 text-sm">
          <div className="font-medium">{data.driverName ?? '—'}</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Shift {data.shiftDate} {data.vehiclePlate ? '· ' + data.vehiclePlate : ''}
          </div>
        </div>

        {/* Computed totals */}
        <div className="rounded-lg border border-border p-3 space-y-1.5 text-sm">
          <Row label="Trips" value={String(data.tripsCount)} />
          <Row label="Revenue collected" value={fmtSar(data.tripsRevenueSar)} highlight />
          <Row label="Distance" value={data.totalDistanceKm.toFixed(0) + ' km'} />
          <div className="border-t border-border my-1" />
          <Row label="Fuel cost" value={'−' + fmtSar(data.fuelCostSar)} negative />
          <Row label="Expenses"  value={'−' + fmtSar(data.expenseTotalSar)} negative />
          <Row label="Deductions" value={'−' + fmtSar(data.deductionTotalSar)} negative />
          <div className="border-t-2 border-foreground/40 my-1" />
          <Row label="Expected cash" value={fmtSar(data.expectedCashSar)} highlight />
        </div>

        {/* Input */}
        <div className="space-y-1.5">
          <Label htmlFor="actual">Actual cash handed (SAR)</Label>
          <Input id="actual" type="number" step="0.01" inputMode="decimal" value={actual} onChange={(e) => setActual(e.target.value)}
            disabled={submitted ?? false}
            className="h-14 text-2xl font-mono" />
          {!submitted && (
            <div className={'text-sm ' + (flagged ? 'text-rose-600 dark:text-rose-400' : 'text-muted-foreground')}>
              Variance: {variance >= 0 ? '+' : ''}{fmtSar(variance)}
              {flagged && ' — will be flagged for admin review'}
            </div>
          )}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="notes">Notes <span className="text-[10px] normal-case tracking-normal font-normal text-muted-foreground">(optional)</span></Label>
          <textarea id="notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
            disabled={submitted ?? false}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base" />
        </div>

        {submitted ? (
          <div className="rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-400 p-3 text-sm inline-flex items-center gap-2">
            <Check className="h-4 w-4" /> Submitted ({data.existing?.status})
          </div>
        ) : (
          <Button onClick={submit} disabled={busy || !actual} className="w-full gap-2 h-12 text-base">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
            Submit closing
          </Button>
        )}
      </main>
    </div>
  );
}

function Row({ label, value, highlight, negative }: { label: string; value: string; highlight?: boolean; negative?: boolean }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className={'text-xs ' + (highlight ? 'text-foreground font-medium' : 'text-muted-foreground')}>{label}</span>
      <span className={'font-mono ' + (negative ? 'text-rose-600 dark:text-rose-400' : highlight ? 'text-foreground font-medium' : '')}>{value}</span>
    </div>
  );
}
